import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion as useProjectReducedMotion } from '../../hooks/useReducedMotion';
import { useStory } from '../../context/StoryContext';

/**
 * TypewriterHeadline — Animated headline typing effect for key claims
 * Types out text character-by-character with cursor, then holds
 * Used in GoingMerry (hero claim), Ramsey (founder statement)
 *
 * Importers: src/pages/GoingMerryPage.jsx (The Claim beat), src/pages/RamseyPage.jsx (The Quote beat)
 * Affected API: Exports TypewriterHeadline, TypewriterWords, TypewriterLines components
 * Data schema: Props accept text (string|string[]), speed (ms/char), delay (ms), loop (boolean), cursorChar (string), showCursor (boolean), beatId (string), className (string), style (object), onComplete (function), as (Component type)
 * User instruction: "see this website is good... but the brand needs some kinda storytelling UI/UX... right? dont u think ? use ur marketing skills and make up a plan"
 */
const MAX_TYPING_MS = 1600;

const TypewriterHeadline = ({
  text,
  speed = 30, // ms per character
  delay = 0,
  className = '',
  style,
  beatId,
  threshold = 0.1,
  rootMargin = '0px',
  onComplete,
  as: Component = 'h1',
  // accepted for older callers; the cursor and looping were removed
  // eslint-disable-next-line no-unused-vars
  loop,
  // eslint-disable-next-line no-unused-vars
  cursorChar,
  // eslint-disable-next-line no-unused-vars
  showCursor,
  ...props
}) => {
  const reducedMotion = useProjectReducedMotion();
  const [state, setState] = useState('idle'); // idle -> revealing -> done
  const elementRef = useRef(null);
  const completed = useRef(false);

  const lines = Array.isArray(text) ? text : [text];
  const totalChars = lines.reduce((n, l) => n + l.length, 0);
  const stepSeconds = Math.min(speed, MAX_TYPING_MS / Math.max(totalChars, 1)) / 1000;

  const finish = () => {
    setState('done');
    if (!completed.current) {
      completed.current = true;
      if (onComplete) onComplete();
    }
  };

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          if (reducedMotion) {
            finish();
          } else {
            setState((s) => (s === 'idle' ? 'revealing' : s));
          }
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(element);
    return () => observer.disconnect();
    // finish only touches refs and stable setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, rootMargin, reducedMotion]);

  // The whole headline is in the page from the start; characters fade in one after another.
  // Words stay together so wrapping never breaks a word, and the heading's text content is the plain text.
  let charIndex = 0;
  const renderLine = (line) =>
    line.split(' ').map((word, wi, words) => (
      <React.Fragment key={wi}>
        <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {[...word].map((ch) => {
            const index = charIndex;
            charIndex += 1;
            const last = index === totalChars - 1;
            return (
              <motion.span
                key={index}
                style={{ display: 'inline-block' }}
                initial={false}
                animate={{ opacity: state === 'idle' ? 0 : 1, y: state === 'idle' ? 6 : 0 }}
                transition={reducedMotion ? { duration: 0 } : { duration: 0.25, delay: delay / 1000 + index * stepSeconds }}
                onAnimationComplete={last && state === 'revealing' ? finish : undefined}
              >
                {ch}
              </motion.span>
            );
          })}
        </span>
        {wi < words.length - 1 && ' '}
      </React.Fragment>
    ));

  // Spaces between words are not characters in the reveal order
  const MotionTag = motion[Component] || motion.div;
  return (
    <MotionTag ref={elementRef} data-state={state} data-beat-id={beatId} aria-label={lines.join(' ')} className={className} style={style} {...props}>
      {lines.map((line, li) => (
        <span key={li} aria-hidden="true" style={{ display: lines.length > 1 ? 'block' : 'inline' }}>
          {renderLine(line)}
        </span>
      ))}
    </MotionTag>
  );
};

export const TypewriterWords = ({
  words = [],
  wordDelay = 300,
  delay = 0,
  className = '',
  style,
  beatId,
  threshold = 0.1,
  rootMargin = '0px',
  onComplete,
  as: Component = 'h2',
  ...props
}) => {
  const { reducedMotion, isBeatActive } = useStory();
  const [isVisible, setIsVisible] = useState(false);
  const [displayWords, setDisplayWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const elementRef = useRef(null);
  const isCurrentBeat = beatId ? isBeatActive(beatId) : false;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  useEffect(() => {
    if (!isVisible || reducedMotion || currentWordIndex >= words.length) {
      if (currentWordIndex >= words.length) {
        onComplete?.();
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      setDisplayWords((prev) => [...prev, words[currentWordIndex]]);
      setCurrentWordIndex((prev) => prev + 1);
    }, currentWordIndex === 0 ? delay : wordDelay);

    return () => clearTimeout(timeoutId);
  }, [isVisible, currentWordIndex, words, wordDelay, delay, onComplete, reducedMotion]);

  if (reducedMotion) {
    return (
      <Component
        ref={elementRef}
        className={`typewriter-words ${isVisible ? 'is-visible' : ''} ${isCurrentBeat ? 'beat-active' : ''} ${className}`}
        style={{ opacity: isVisible ? 1 : 0, ...style }}
        {...props}
      >
        {words.join(' ')}
      </Component>
    );
  }

  return (
    <Component
      ref={elementRef}
      className={`typewriter-words ${isCurrentBeat ? 'beat-active' : ''} ${className}`}
      style={style}
      {...props}
      aria-live="polite"
    >
      <span style={{ display: 'inline-flex', gap: '0.25em' }}>
        {displayWords.map((word, index) => (
          <motion.span
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 180 }}
          >
            {word}
          </motion.span>
        ))}
      </span>
    </Component>
  );
};

/**
 * TypewriterLines — Line-by-line reveal for multi-line statements
 * Reveals full lines with stagger, for manifesto-style copy
 */
export const TypewriterLines = ({
  lines = [],
  lineDelay = 400,
  delay = 0,
  className = '',
  style,
  beatId,
  threshold = 0.1,
  rootMargin = '0px',
  onComplete,
  as: Component = 'div',
  ...props
}) => {
  const { reducedMotion, isBeatActive } = useStory();
  const [isVisible, setIsVisible] = useState(false);
  const [displayLines, setDisplayLines] = useState([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const elementRef = useRef(null);
  const isCurrentBeat = beatId ? isBeatActive(beatId) : false;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  useEffect(() => {
    if (!isVisible || reducedMotion || currentLineIndex >= lines.length) {
      if (currentLineIndex >= lines.length) {
        onComplete?.();
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      setDisplayLines((prev) => [...prev, lines[currentLineIndex]]);
      setCurrentLineIndex((prev) => prev + 1);
    }, currentLineIndex === 0 ? delay : lineDelay);

    return () => clearTimeout(timeoutId);
  }, [isVisible, currentLineIndex, lines, lineDelay, delay, onComplete, reducedMotion]);

  if (reducedMotion) {
    return (
      <Component
        ref={elementRef}
        className={`typewriter-lines ${isVisible ? 'is-visible' : ''} ${isCurrentBeat ? 'beat-active' : ''} ${className}`}
        style={{ opacity: isVisible ? 1 : 0, ...style }}
        {...props}
      >
        {lines.map((line, index) => (
          <p key={index} style={{ margin: '0.5rem 0' }}>{line}</p>
        ))}
      </Component>
    );
  }

  return (
    <Component
      ref={elementRef}
      className={`typewriter-lines ${isCurrentBeat ? 'beat-active' : ''} ${className}`}
      style={style}
      {...props}
      aria-live="polite"
    >
      {displayLines.map((line, index) => (
        <motion.p
          key={index}
          style={{ margin: '0.5rem 0' }}
          initial={{ opacity: 0, y: 16, filter: 'blur(4px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ type: 'spring', damping: 22, stiffness: 160, mass: 0.8 }}
        >
          {line}
        </motion.p>
      ))}
    </Component>
  );
};

export { TypewriterHeadline };
export default TypewriterHeadline;